import React, { useState } from 'react';
import { AUTH_CONFIG } from '../../config/authConfig';
import { useAuth } from '../../contexts/AuthContext';
import './AuthModal.css';

interface SignupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToLogin: () => void;
}

const SignupModal: React.FC<SignupModalProps> = ({
  isOpen,
  onClose,
  onSwitchToLogin
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [acceptCommunications, setAcceptCommunications] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const { signUp, signInWithProvider } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);


    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    const { error } = await signUp(email, password, {
      first_name: firstName,
      last_name: lastName,
      accept_communications: acceptCommunications
    });
    
    if (error) {
      setError(error.message);
    } else {
      setSuccess(true);
    }
    
    setLoading(false);
  };

  const handleProviderSignIn = async (provider: 'google') => {
    setLoading(true);
    setError(null);

    const { error } = await signInWithProvider(provider);
    
    if (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  if (success) {
    return (
      <div className="auth-modal-overlay" onClick={onClose}>
        <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
          <div className="auth-modal-header">
            <h2>Account Created Successfully!</h2>
            <button className="auth-modal-close" onClick={onClose}>×</button>
          </div>
          <div className="auth-modal-body">
            <div className="auth-success">
              <p>Your account has been created successfully!</p>
              <p>You can now sign in and start using ClimatePlans.org.</p>
              {!AUTH_CONFIG.EMAIL_VERIFICATION_REQUIRED && (
                <p><em>Note: Email verification is temporarily disabled for testing purposes.</em></p>
              )}
            </div>
            <div className="auth-modal-footer">
              <button
                className="auth-button auth-button-primary"
                onClick={onClose}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <div className="auth-modal-header">
          <h2>Create Account</h2>
          <button className="auth-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="auth-modal-body">
          {error && (
            <div className="auth-error">
              {error}
            </div>
          )}

          {/* Google Sign-in First */}
          <div className="auth-providers">
            <button
              className="auth-provider-button auth-provider-google"
              onClick={() => handleProviderSignIn('google')}
              disabled={loading}
            >
              <svg className="auth-provider-icon" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>
          </div>

          <div className="auth-divider">
            <span>or</span>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="auth-form-row">
              <div className="auth-form-group">
                <label htmlFor="firstName">First Name</label>
                <input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  disabled={loading}
                  placeholder="First name"
                />
              </div>

              <div className="auth-form-group">
                <label htmlFor="lastName">Last Name</label>
                <input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  disabled={loading}
                  placeholder="Last name"
                />
              </div>
            </div>

            <div className="auth-form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                placeholder="Enter your email"
              />
            </div>


            <div className="auth-form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                placeholder="Create a password (min. 6 characters)"
                minLength={6}
              />
            </div>

            <div className="auth-form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                placeholder="Confirm your password"
                minLength={6}
              />
            </div>

            <div className="auth-form-group">
              <p className="auth-terms-note">
                By creating an account, you agree to our{' '}
                <a 
                  href="https://coolclimate.berkeley.edu/files2/CoolClimate-terms-of-use.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="auth-link"
                >
                  Terms of Use and Privacy Statement
                </a>
                . You'll be asked to accept these terms when completing your profile.
              </p>
            </div>

            <div className="auth-form-group">
              <label style={{ 
                display: 'flex', 
                alignItems: 'flex-start',
                cursor: 'pointer',
                gap: '8px',
                fontSize: '0.9rem'
              }}>
                <input
                  type="checkbox"
                  checked={acceptCommunications}
                  onChange={(e) => setAcceptCommunications(e.target.checked)}
                  disabled={loading}
                  style={{
                    marginTop: '4px',
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer',
                    flexShrink: 0
                  }}
                />
                <span>
                  I agree to receive communications from ClimatePlans. We will not share contact information with third parties.
                </span>
              </label>
            </div>

            <button
              type="submit"
              className="auth-button auth-button-primary"
              disabled={loading}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>


          <div className="auth-modal-footer">
            <p>
              Already have an account?{' '}
              <button
                className="auth-link-button"
                onClick={onSwitchToLogin}
                disabled={loading}
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      </div>

    </div>
  );
};

export default SignupModal;
